import { Component, Input, OnInit, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { takeUntil, tap } from 'rxjs/operators';

import { QueryStringTypesEnum } from 'src/app/configs/query-string-types';
import {
  ComparisonOperator,
  GetPaymentsDto,
  GetResellerPaymentWithPaginationDto,
  PaginatedListOfGetPaymentsDto,
  PaginatedListOfGetResellerPaymentWithPaginationDto,
  TypePaymentStatus,
} from 'src/app/core/api/models';
import { EventTypes } from 'src/app/core/constants/event-types';
import { TextPopoverDirective } from 'src/app/core/directives/text-popover.directive';
import { confirmDialogConfig } from 'src/app/core/services/dialog.service';
import { AppDomService } from 'src/app/core/services/dom.service';
import { BaseIndexModule } from 'src/app/pages/shared/base/modules/base-index.module';
import {
  ExcelService,
  PaymentsService,
  ResellersService,
} from '../../../../core/api/services';
import { DynamicTabService } from '../../../../core/services/dynamic-tab.service';
import { UtilityService } from '../../../../core/services/utility.service';
import { BaseIndexPageComponent } from '../../../shared/base/components/base-index-page.component';
import { GetEnumValuesDto } from './../../../../core/api/models/get-enum-values-dto';
import { BasePaymentDetailsComponent } from './base-payment-details/base-payment-details.component';
import { CreatePaymentsExcelDialogComponent } from './create-payments-excel/create-payments-excel-dialog.component';
import { ShowPaymentImageModalComponent } from './show-payment-image-modal/show-payment-image-modal.component';

@Component({
  selector: 'app-base-payments',
  templateUrl: './base-payments.component.html',
  styles: [
    `
      :host-context(.dark) {
        .text-vertical {
          color: white !important;
        }
      }
    `,
  ],
  imports: [BaseIndexModule, BasePaymentDetailsComponent, TextPopoverDirective],
})
export class BasePaymentsComponent
  extends BaseIndexPageComponent
  implements OnInit
{
  //#region Properties
  appLang: string = 'fa';
  fields = 'pages.payments.fields.';
  pageName = 'payments';

  isShowDetailPage: boolean = false;
  detailPageModel: any = {};
  totalSum: number;
  totalSmsCount: number;

  @Input() getTableDataApiMethodName: string;
  @Input() pageTitle: string;
  @Input() showOperationBtns: boolean = false;

  protected currentService = inject(PaymentsService);

  modelWithPagination$: Observable<
    | PaginatedListOfGetPaymentsDto
    | PaginatedListOfGetResellerPaymentWithPaginationDto
  >;

  items$: Observable<
    Array<GetPaymentsDto | GetResellerPaymentWithPaginationDto>
  >;

  TypeBanksEnumValues: GetEnumValuesDto[] = [];
  TypePaymentStatusEnumValues: GetEnumValuesDto[] = [];
  TypePaymentsEnumValues: GetEnumValuesDto[] = [];

  get TypePaymentStatus() {
    return TypePaymentStatus;
  }
  userId: number;

  //#endregion

  constructor(
    public resellerService: ResellersService,
    protected utilityService: UtilityService,
    public dynamicTabService: DynamicTabService,
    public excelFileService: ExcelService
  ) {
    super();
  }

  ngOnInit() {
    try {
      super.ngOnInit();
      this.pageConfig.title = this.pageTitle;
    } catch (error) {
      this.loggerService.logError(this.tagName, error, EventTypes.financial);
    }
  }

  setPageAfterEnterUrl() {
    super.setPageAfterEnterUrl();
    // وارد میشود چک میکنیم که کدام جدول باید نمایش داده شود url زمانی که کاربر با
    let params = this.routingState.parseQueryString();
    for (const [key, value] of Object.entries(params)) {
      if (key == QueryStringTypesEnum.Details_key)
        this.onShowDetails({}, false, +value);
    }
  }
  showDetails(model: any, setFiltersAndUrl = true, id = 0) {
    if (this.items$) this.onShowDetails({}, false, id);
    else
      setTimeout(() => {
        this.showDetails({}, false, id);
      }, 2000);
  }

  getEnumsValues() {
    this.getEnumValuesApi('TypePayments')
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((values) => (this.TypePaymentsEnumValues = values));
    this.getEnumValuesApi('TypeBanks')
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((values) => (this.TypeBanksEnumValues = values));
    this.getEnumValuesApi('TypePaymentStatus')
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((values) => (this.TypePaymentStatusEnumValues = values));
  }

  configTableColumns() {
    this.tableVocabs = this.pageVocabs + 'fields.';

    this.tableColumns = [
      {
        title: this.localizer.id,
        name: 'id',
        hasSearch: true,
        type: 'number',
        wrap: true,
        noNumberSeparator: true,
      },
      {
        title: this.localizer.amount,
        name: 'amount',
        hasSearch: true,
        needCurrency: true,
        type: 'number',
        wrap: true,
      },

      {
        title: `${this.tableVocabs}smsCount`,
        name: 'smsCount',
        type: 'number',
        hasSearch: false,
        noSorting: true,
        wrap: true,
      },
      {
        title: `${this.tableVocabs}typePayment`,
        name: 'typePayment',
        hasSearch: true,
        type: 'enum',
        enumName: 'TypePayments',
        wrap: true,
      },
      {
        title: `${this.tableVocabs}isOnlineGeneral`,
        name: 'isOnlineGeneral',
        type: 'boolean',
        hasSearch: true,
        wrap: true,
      },
      {
        title: `${this.tableVocabs}payerOrganizationName`,
        name: 'payerOrganizationName',
        hasSearch: true,
        wrap: true,
        customOperators: [
          ComparisonOperator.StartsWith,
          ComparisonOperator.NotEqualTo,
          ComparisonOperator.EqualTo,
          ComparisonOperator.Contains,
          ComparisonOperator.EndsWith,
        ],
      },
      {
        title: `${this.tableVocabs}userName`,
        name: 'userName',
        hasSearch: true,
        wrap: true,
        customOperators: [
          ComparisonOperator.StartsWith,
          ComparisonOperator.NotEqualTo,
          ComparisonOperator.EqualTo,
          ComparisonOperator.Contains,
          ComparisonOperator.EndsWith,
        ],
      },
      {
        title: `${this.tableVocabs}receiverOrganizationName`,
        name: 'receiverOrganizationName',
        hasSearch: true,
        wrap: true,
        customOperators: [
          ComparisonOperator.StartsWith,
          ComparisonOperator.NotEqualTo,
          ComparisonOperator.EqualTo,
          ComparisonOperator.Contains,
          ComparisonOperator.EndsWith,
        ],
      },
      {
        title: `${this.tableVocabs}accountInformationAccountNo`,
        name: 'accountInformationAccountNo',
        hasSearch: true,
        wrap: true,
        dir: 'ltr',
        customOperators: [
          ComparisonOperator.StartsWith,
          ComparisonOperator.NotEqualTo,
          ComparisonOperator.EqualTo,
          ComparisonOperator.Contains,
          ComparisonOperator.EndsWith,
        ],
      },
      {
        title: 'common.owner',
        name: 'accountInformationOwner',
        hasSearch: true,
        wrap: true,
        customOperators: [
          ComparisonOperator.StartsWith,
          ComparisonOperator.NotEqualTo,
          ComparisonOperator.EqualTo,
          ComparisonOperator.Contains,
          ComparisonOperator.EndsWith,
        ],
      },
      {
        title: this.localizer.status,
        name: 'typeStatus',
        hasSearch: true,
        type: 'enum',
        enumName: 'TypePaymentStatus',
        wrap: true,
      },
      {
        title: `${this.tableVocabs}isHaveImage`,
        name: 'isHaveImage',
        hasSearch: true,
        type: 'boolean',
        wrap: true,
      },
      {
        title: `${this.tableVocabs}smsCountPerPriceDescription`,
        name: 'smsCountPerPriceDescription',
        hasSearch: false,
        noSorting: true,
        wrap: true,
      },
      {
        title: `${this.tableVocabs}paymentDate`,
        name: 'paymentDate',
        hasSearch: true,
        type: 'dateTime',
        wrap: true,
      },
      {
        title: this.localizer.createDate,
        name: 'created',
        hasSearch: true,
        type: 'dateTime',
        wrap: true,
      },
    ];
  }

  //#region api methods

  //Get All
  getPaginationApi(): Observable<any> {
    try {
      return this.currentService[this.getTableDataApiMethodName]({
        body: {
          ...this.getBaseModelWithPaginationParams(),
          userId: this.userId,
        },
      }).pipe(tap((items: any) => this.calculateTotalSum(items?.data?.items)));
    } catch (error) {
      this.loggerService.logError(this.tagName, error, EventTypes.financial);
    }
  }

  PaymentsExcelReportApi() {
    this.excelFileService
      .excelCreatePaymentsExcelGet()
      .pipe(this.basePipeOperators())
      .subscribe((res) => {
        if (res) {
          let data = {
            title: `${this.pageVocabs}paymentsExcel`,
            comparisonObjects: this.comparisonObjects,
            sortObject: this.sortObject,
          };

          const modalResult = this.dialogService.openModal({
            component: CreatePaymentsExcelDialogComponent,
            data: data,
          });

          modalResult
            .afterClosed()
            .pipe(
              takeUntil(this.onDestroy$),
              tap(() => {
                AppDomService.removeModalScrollClassFromHtml();
              })
            )
            .subscribe();
        }
      });
  }

  //only for super admin
  confirmPayment(paymentId: number) {
    this.unSubs();

    this.pageConfig.loading = true;

    if (this.pageConfig.loading)
      this.currentService
        .paymentsConfirmPayment({ body: { selectedPaymentId: paymentId } })
        .pipe(this.basePipeOperators())
        .subscribe(this.baseObserver);
  }

  //only for super admin
  rejectPayment(paymentId: number) {
    this.unSubs();

    this.pageConfig.loading = true;

    if (this.pageConfig.loading)
      this.currentService
        .paymentsRejectPayment({ body: { selectedPaymentId: paymentId } })
        .pipe(this.basePipeOperators())
        .subscribe(this.baseObserver);
  }

  //#endregion

  calculateTotalSum(items: any[]) {
    try {
      this.totalSum = items
        .map((item) => item.amount)
        .reduce((acc, curr) => {
          return acc + curr;
        }, 0);
      this.totalSmsCount = items
        .map((item) => item.smsCount)
        .reduce((acc, curr) => {
          return acc + curr;
        }, 0);
    } catch (error) {
      this.loggerService.logError(this.tagName, error, EventTypes.financial);
    }
  }

  onCreate() {
    try {
      this.dynamicTabService.setNewTab('');
      this.dynamicTabService.setNewTab('newPayment');
      this.dynamicTabService.setActiveTab('newPayment');
    } catch (error) {
      this.loggerService.logError(this.tagName, error, EventTypes.financial);
    }
  }

  onShowDetails(model: any, setFiltersAndUrl = true, id = 0) {
    this.showParent = false;
    this.setParentQueryString();
    this.isShowDetailPage = true;
    if (setFiltersAndUrl) {
      this.location.go(
        window.location.pathname +
          '?' +
          QueryStringTypesEnum.Details_key +
          '=' +
          model.id
      );
      this.detailPageModel = model;
    } else {
      this.items$.subscribe(
        (items) => {
          this.detailPageModel = {
            ...items.find((item) => item.id == id),
          };
        },
        (error) => {
          console.error('Error:', error);
        }
      );
    }
  }

  onExcelReport() {
    this.PaymentsExcelReportApi();
  }

  //#region confirm and reject payment modal
  //confirm
  onConfirm(paymentId: number) {
    try {
      const config: confirmDialogConfig = {
        title: 'messages.confirmPayment',
        content: paymentId.toString(),
      };

      const dialogResult = this.getConfirmDialog(config);

      dialogResult
        .afterClosed()
        .pipe(takeUntil(this.onDestroy$))
        .subscribe((result) => {
          if (result) this.confirmPayment(paymentId);
        });
    } catch (error) {
      this.loggerService.logError(this.tagName, error, EventTypes.financial);
    }
  }

  //reject
  onReject(paymentId: number) {
    try {
      const config: confirmDialogConfig = {
        title: 'messages.rejectPayment',
        content: paymentId.toString(),
        confirmBtnColor: 'btn-danger',
      };

      const dialogResult = this.getConfirmDialog(config);

      dialogResult
        .afterClosed()
        .pipe(takeUntil(this.onDestroy$))
        .subscribe((result) => {
          if (result) this.rejectPayment(paymentId);
        });
    } catch (error) {
      this.loggerService.logError(this.tagName, error, EventTypes.financial);
    }
  }

  onShowImage(id) {
    this.currentService
      .paymentsDownloadPaymentImage({ PaymentId: id })
      .pipe(this.basePipeOperators())
      .subscribe((res) => {
        const modalResult = this.dialogService.openModal({
          component: ShowPaymentImageModalComponent,
          data: res?.data,
        });

        modalResult
          .afterClosed()
          .pipe(
            takeUntil(this.onDestroy$),
            tap(() => {
              AppDomService.removeModalScrollClassFromHtml();
            })
          )
          .subscribe();
      });
  }
  //#endregion

  onChangeUser(userId: number) {
    this.userId = userId;
    this.getTableData();
  }

}
